import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FileModel } from 'src/app/shared/file/file.models';
import { DashboardNotificationService } from 'src/app/shared/notification/notification.service';
import { FileSizePipe } from 'src/app/shared/file/file-size.pipe';
import { FileViewComponent } from '../file-view/file-view.component';

@Component({
  standalone: false,
  selector: 'app-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss']
})
export class FileInputComponent {
  private DEFAULT_MAX_FILES_COUNT = 5;
  private DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024;

  @Input()
  get files(): FileModel[] {
    return this.filesValue;
  }
  set files(value: FileModel[]) {
    this.filesValue = [...value];
  }
  filesValue: FileModel[] = [];

  @Input()
  placeholder?: string;

  @Input()
  template?: 'default' | 'claim';

  @Input()
  textUnderDropZone?: string;

  @Input()
  isViewOnly = false;

  @Input()
  maxFileSize?: number;

  @Input()
  maxFilesCount?: number;

  @Input()
  fileType?: number;

  @Input()
  accept = '.jpeg, .jpg, .png, .bmp, .pdf';

  @Output()
  filesChange = new EventEmitter<FileModel[]>();

  get uploadAvailable() {
    return this.maxFilesCount ?
      this.files.length < this.maxFilesCount :
      this.files.length < this.DEFAULT_MAX_FILES_COUNT;
  }

  constructor(
    private dialog: MatDialog,
    private notificationService: DashboardNotificationService,
    private fileSizePipe: FileSizePipe) {
  }

  onFileInputChange(event: any) {
    const eventFiles = event.target.files as FileList;
    if (!this.allEventFilesValid(eventFiles)) {
      return;
    }

    this.pushFiles(eventFiles);
  }

  onDragAndDropFile(event: any) {
    const eventFiles = event as FileList;
    if (!this.allEventFilesValid(event)) {
      return;
    }

    this.pushFiles(eventFiles);
  }

  onReplaceFile(file: FileModel, event: any) {
    if (!file) {
      return;
    }

    const eventFiles = event.target.files as FileList;
    if (!this.allEventFilesValid(eventFiles, true)) {
      return;
    }

    this.detectUrl(eventFiles[0])
      .then(url => {
        if (url) {
          const fileIndex = this.files.findIndex(x => x === file);
          this.files[fileIndex] = {
            url: url,
            name: eventFiles[0].name,
            typeCode: this.fileType !== undefined && this.fileType !== null ? this.fileType : 0
          };
          this.filesChange.emit(this.files);
        }
      });
  }

  onDeleteFile(file: FileModel) {
    if (!file) {
      return;
    }
    const fileIndex = this.files.findIndex(x => x === file);
    if (fileIndex > -1) {
      this.files.splice(fileIndex, 1)
    }
    this.filesChange.emit(this.files);
  }

  onFileInputClick(event: any) {
    event.target.value = null;
  }

  onOpenFile(file: FileModel) {
    if (this.isPdf(file)) {
      this.openPdfFile(file);
      return;
    }

    this.dialog.open(FileViewComponent, {
      panelClass: 'qira-file-view-dialog',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      autoFocus: false,
      data: file
    });
  }

  isViewAvailable(file: FileModel) {
    const extension = this.getFileExtension(file);
    return ['png', 'gif', 'jpg', 'jpeg', 'bmp', 'pdf'].includes(extension);
  }

  trackByFile(index: number, file: FileModel) {
    return file.url || `${file.typeCode ?? ''}:${file.name ?? ''}:${index}`;
  }

  truncateFilename(filename: string, baseMax = 10, ellipsis = '...'): string {
    if (!filename) return '';
    const { base, extWithDot } = this.splitFilename(filename);
    if (base.length <= baseMax) return filename;
    return base.slice(0, baseMax) + ellipsis + extWithDot;
  }

  private splitFilename(name: string): { base: string; extWithDot: string } {
    const i = name.lastIndexOf('.');
    if (i <= 0) return { base: name, extWithDot: '' };
    return { base: name.slice(0, i), extWithDot: name.slice(i) };
  }

  private isPdf(file: FileModel) {
    return this.getFileExtension(file) === 'pdf';
  }

  private getFileExtension(file: FileModel) {
    return file?.name?.split('.').pop()?.toLowerCase() ?? '';
  }

  private allEventFilesValid(eventFiles: FileList, isReplace?: boolean): boolean {
    if (!eventFiles || eventFiles.length < 1) {
      return false;
    }

    const availableFilesCount = this.files.length + eventFiles.length;
    const maxFilesCount = this.maxFilesCount || this.DEFAULT_MAX_FILES_COUNT;

    if (availableFilesCount > maxFilesCount && !isReplace) {
      this.notificationService.error(`The maximum number of files is ${maxFilesCount}`);
      return false;
    }

    for (let i = 0; i < eventFiles.length; i++) {
      const fileSize = eventFiles[i].size;
      const maxFileSize = this.maxFileSize || this.DEFAULT_MAX_FILE_SIZE;
      if (fileSize > maxFileSize) {
        const errorMessage = `File size cannot be greater than ${this.fileSizePipe.transform(maxFileSize)}`;
        this.notificationService.error(errorMessage);
        return false;
      }
    }

    for (let i = 0; i < eventFiles.length; i++) {
      const lastDotIndex = eventFiles[i].name.lastIndexOf('.');
      if (lastDotIndex === -1) {
        return false;
      }
      const fileExtension = eventFiles[i].name.substring(lastDotIndex + 1);
      if (!this.accept.includes(fileExtension)) {
        const errorMessage = `File type not supported. Please upload a file with one of the following extensions: ${this.accept.replace(/\.([a-z]+)/g, '.$1').replace(/, /g, ', ')}`;
        this.notificationService.error(errorMessage);
        return false;
      }
    }

    return true;
  }

  private detectUrl(file: File) {
    return new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result as string);
      };
    });
  }

  private pushFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      this.detectUrl(files[i])
        .then(url => {
          if (url) {
            const file: FileModel = {
              url: url,
              name: files[i].name,
              typeCode: this.fileType !== undefined && this.fileType !== null ? this.fileType : 0
            };

            this.files.push(file);
            this.filesChange.emit(this.files);
          }
        });
    }
  }

  private openPdfFile(file: FileModel) {
    if (file.url?.includes(';base64,')) {
      const pdfWindow = window.open('');
      pdfWindow!.document.write(`<iframe width='100%' height='100%' src='${file.url}'></iframe>`);
      return;
    }
    window.open(file.url);
  }
}
